// @ts-nocheck
import { resolveColor, safeColor } from "@insite/mobius/utilities";
import { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Props";
import { buttonStyles } from "@store/Themes/common/mixins/button";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { ButtonType, MobiusButtonType } from "@toolkit/Framework/Types/Button";
import Styleguide from "@toolkit/Framework/Types/Styleguide";
import { buttonGetHoverProps } from "@toolkit/Themes/common/props";
import { css } from "styled-components";

/* ------------------------------------------------------------------------------------------ */
/* Related Extensions => src/Extensions/Mobius/Button --------------------------------------- */
/* ------------------------------------------------------------------------------------------ */

interface ButtonOptions {
    color: string;
    type: ButtonType;
    hoverColor?: string;
    hoverTextColor?: string;
}

const getButton = (options: ButtonOptions): ButtonPresentationProps => ({
    shape: "rounded",
    buttonType: options.type as MobiusButtonType,
    typographyProps: { weight: "inherit" },
    color: options.color,
    css: buttonStyles(options.type),
    ...buttonGetHoverProps(options.hoverColor || `${options.color}.dark`),
    hoverTextColor: options.hoverTextColor,
});

interface SelectionButtonOptions {
    color: string;
    hoverColor: string;
    textColor?: string;
    showBackground?: boolean;
}

const getSelectionButton = (options: SelectionButtonOptions = { color: "", hoverColor: "", showBackground: true }) =>
    mergeProps<ButtonPresentationProps>(
        getButton({ color: options.color, type: "outline", hoverColor: options.hoverColor }),
        {
            css: css`
                max-width: 100%;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;

                ${({ theme }) => {
                    return css`
                        ${cssProp("color", options.textColor, x => resolveColor(x, theme))}
                        ${options.showBackground
                            ? `background-color: ${safeColor(resolveColor(options.color, theme)).alpha(0.1)};`
                            : null}
                    font-weight: ${theme.design.typography.weight.base};

                        &:disabled {
                            background-color: ${safeColor(theme.colors.button.disabled.contrast).alpha(0.1).toString()};
                        }
                    `;
                }};
            `,
        },
    );

const button: Styleguide["button"] = {
    primary: getButton({
        color: "primary",
        type: "solid",
        hoverColor: "secondary" /* Store Custom Code - hoverColor */,
    }),
    secondary: getButton({ color: "secondary", type: "solid" }),
    tertiary: getButton({ color: "primary", type: "outline" }),
    inverted: getButton({ color: "invert", type: "solid", hoverTextColor: "invert.hover" }),
    invertedOutline: getButton({ color: "invert", type: "outline" }),
    selected: getSelectionButton({ color: "swatch.selected", hoverColor: "swatch.selectedHover" }),
    unselected: getSelectionButton({
        color: "swatch",
        hoverColor: "swatch.hover",
        textColor: "swatch.contrast",
        showBackground: false,
    }),
};

export default button;
